import {
  BooleanInstruction,
  CallToAction,
  FromSettingsInstruction,
  MenuItemDataColumns,
  NavigateToPathAction,
  NavigateToRouteAction,
  NumberInstruction,
  ObjectInstruction,
  SelectionMenu,
  StringInstruction,
  StringListInstruction,
  TitleSelector,
  SettingsPayload,
  PageTitleSelector,
  OpenModalAction,
  Params,
  MenuItemWithContext,
  ListBoxMenuItem,
  GroupedListBoxMenuItem,
} from '../../types';
import { ComponentFactory, ComponentOptions } from './component';
import { ModalDslFactory, ModalDslOptions } from './modal';
import {
  ContentFactory,
  ContentOptions,
  KeyIndicatorOptions,
  KeyIndicatorsFactory,
  LeaderboardFactory,
  LeaderboardOptions,
  MultiKpiFactory,
  MultiKpiOptions,
  SegmentationFactory,
  SegmentationOptions,
  StatsTableFactory,
  StatsTableOptions,
  TimeSeriesFactory,
  TimeSeriesOptions,
  KpiTrendsOptions,
  KpiTrendsFactory,
  BillboardFactory,
  BillboardOptions,
  AlertPanelFactory,
  AlertPanelOptions,
  SingleKpiTrendOptions,
  SingleKpiTrendFactory,
  HeatmapFactory,
  HeatmapOptions,
  KpiDecompositionFactory,
  KpiDecompositionOptions,
} from './content';
import { DataOptionsFactory } from './data';
import { PageDslFactory, PageDslOptions } from './page';
import { ColumnSectionFactory, ColumnSectionOptions, TabbedSectionFactory, TabbedSectionOptions } from './section';

export class CoreFactories {
  private constructor() {}

  static readonly page = {
    dsl(options: PageDslOptions): PageDslFactory {
      return new PageDslFactory(options);
    },
    title: {
      selector(option: PageTitleSelector) {
        return option;
      },
    },
  };

  static modal(options: ModalDslOptions): ModalDslFactory {
    return new ModalDslFactory(options);
  }

  static readonly sections = {
    column(options: ColumnSectionOptions) {
      return new ColumnSectionFactory(options);
    },
    tabbed(options: TabbedSectionOptions) {
      return new TabbedSectionFactory(options);
    },
  };

  static readonly component = {
    dsl(name: string, options: ComponentOptions): ComponentFactory {
      return new ComponentFactory(name, options);
    },
    title: {
      selector(options: TitleSelector): TitleSelector {
        return options;
      },
    },
    callToAction(options: CallToAction): CallToAction {
      return options;
    },
  };

  static readonly data = {
    asProvider: DataOptionsFactory.asProvider,
    withData: DataOptionsFactory.withData,
    asReceiver: DataOptionsFactory.asReceiver,
  };

  static readonly content = {
    custom(type: string, options?: ContentOptions): ContentFactory {
      return new ContentFactory(type, options);
    },
    leaderboard(options: LeaderboardOptions): LeaderboardFactory {
      return new LeaderboardFactory(options);
    },
    multiKpi(options: MultiKpiOptions): MultiKpiFactory {
      return new MultiKpiFactory(options);
    },
    segmentation(options: SegmentationOptions): SegmentationFactory {
      return new SegmentationFactory(options);
    },
    statsTable(options: StatsTableOptions): StatsTableFactory {
      return new StatsTableFactory(options);
    },
    timeSeries(options: TimeSeriesOptions): TimeSeriesFactory {
      return new TimeSeriesFactory(options);
    },
    keyIndicators(options: KeyIndicatorOptions): KeyIndicatorsFactory {
      return new KeyIndicatorsFactory(options);
    },
    kpiTrends(options: KpiTrendsOptions): KpiTrendsFactory {
      return new KpiTrendsFactory(options);
    },
    singleKpiTrend(options: SingleKpiTrendOptions): SingleKpiTrendFactory {
      return new SingleKpiTrendFactory(options);
    },
    billboard(options: BillboardOptions): BillboardFactory {
      return new BillboardFactory(options);
    },
    alertPanel(options: AlertPanelOptions): AlertPanelFactory {
      return new AlertPanelFactory(options);
    },
    heatmap(options: HeatmapOptions): HeatmapFactory {
      return new HeatmapFactory(options);
    },
    kpiDecomposition(options: KpiDecompositionOptions): ContentFactory {
      return new KpiDecompositionFactory(options);
    },
  };

  static readonly contentInstruction = {
    fromSettingInstruction(settingName: string): FromSettingsInstruction {
      return {
        type: 'FromSettingsInstruction',
        instruction: settingName,
      };
    },
    stringInstruction(value: string): StringInstruction {
      return {
        type: 'StringInstruction',
        instruction: value,
      };
    },
    stringListInstruction(value: string[]): StringListInstruction {
      return {
        type: 'StringListInstruction',
        instruction: value,
      };
    },
    booleanInstruction(value: boolean): BooleanInstruction {
      return {
        type: 'BooleanInstruction',
        instruction: value,
      };
    },
    numberInstruction(value: number): NumberInstruction {
      return {
        type: 'NumberInstruction',
        instruction: value,
      };
    },
    objectInstruction(value: unknown): ObjectInstruction {
      return {
        type: 'ObjectInstruction',
        instruction: value,
      };
    },
  };

  static readonly uiAction = {
    navigateToPath(path: string, settings?: SettingsPayload): NavigateToPathAction {
      return {
        type: 'NAVIGATE_TO_PATH',
        payload: {
          path,
          settings,
        },
      };
    },
    navigateToRoute(slug: string, args?: Record<string, string[]>, settings?: SettingsPayload): NavigateToRouteAction {
      return {
        type: 'NAVIGATE_TO_ROUTE',
        payload: {
          slug,
          args,
          settings,
        },
      };
    },
    openModal(name: string, endpoint: string, params?: Params): OpenModalAction {
      return {
        type: 'OPEN_MODAL',
        payload: {
          name,
          endpoint,
          params,
        },
      };
    },
  };

  static readonly selectionMenu = {
    menu(options: SelectionMenu): SelectionMenu {
      return options;
    },
    item(options: Omit<ListBoxMenuItem, 'type'>): ListBoxMenuItem {
      return {
        type: 'MenuItem',
        ...options,
      };
    },
    items(options: Omit<ListBoxMenuItem, 'type'>[]): ListBoxMenuItem[] {
      return options.map(option => ({
        type: 'MenuItem',
        ...option,
      }));
    },
    groupedItem(options: Omit<GroupedListBoxMenuItem, 'type'>): GroupedListBoxMenuItem {
      return {
        type: 'MenuItemGrouped',
        ...options,
      };
    },
    groupedItems(options: Omit<GroupedListBoxMenuItem, 'type'>[]): GroupedListBoxMenuItem[] {
      return options.map(option => ({
        type: 'MenuItemGrouped',
        ...option,
      }));
    },
    columns(options: Omit<MenuItemDataColumns, 'type'>): MenuItemDataColumns {
      return {
        type: 'DataColumns',
        ...options,
      };
    },
    itemWithContext(options: Omit<MenuItemWithContext, 'type'>): MenuItemWithContext {
      return {
        type: 'MenuItemContext',
        ...options,
      };
    },
  };
}

export type { SectionFactory } from './section';
export type { ContentFactory } from './content';
